import { useEffect, useRef, useState } from 'react'

import { faXmark } from '@fortawesome/pro-regular-svg-icons'
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'

import { useDecodedAccessToken } from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  activeSellerSubscriptionServiceQuery,
  getSellerCollectsConfig,
  type OrchestratedAuctionListingDetails,
  type OrchestratedOfferingListingDetails,
} from '@b-stock/fe-core/bstock-next/queries'
import { FormattedMessage } from '@b-stock/fe-core/bstock-react'
import { Colors, Typography } from '@b-stock/fe-core/design-system'

import { useAcceptedPaymentMethods } from '@/buyer-portal/components/features/listing/acceptedPaymentMethods'
import {
  buyerPremiumAppliesFor,
  sellerCollectsListingContext,
  useSellerCollectsAnalytics,
} from '@/buyer-portal/helpers/sellerCollectsAnalytics'
import { getFeeModel } from '@/buyer-portal/helpers/sellerCollectsFeeModel'
import { getListingSaleKind } from '@/buyer-portal/helpers/sellerCollectsSaleKind'
import { resolveShippingArrangedBy } from '@/buyer-portal/helpers/sellerCollectsShippingMode'
import { useAsksForProofOfPayment } from '@/buyer-portal/helpers/useAsksForProofOfPayment'
import { useHasPriorSellerCollectsOrder } from '@/buyer-portal/helpers/useHasPriorSellerCollectsOrder'
import useIsLoggedIn from '@/buyer-portal/helpers/useIsLoggedIn'
import { FeatureFlags } from '@/helpers/featureFlags'
import { SellerCollectsEvent } from '@/helpers/telemetry/SellerCollectsAnalyticsEvents'

import {
  getSellerCollectsWinStepKeys,
  sellerCollectsWinStepId,
} from '../sellerCollectsWinSteps'
import { bannerCopyId } from './sellerCollectsBannerCopy'
import {
  dismissBanner,
  isBannerDismissed,
} from './sellerCollectsBannerDismissal'

const STEPS_ID = 'seller-collects-banner-steps'

const HEADLINE_ID = 'seller-collects-banner-headline'

// A card in the B-Stock accent, not the design system's info banner: LDP-2
// gives this its own treatment so it reads as an explainer the buyer has not
// seen before, rather than one more blue notice on the page. Tinted, not
// white — on a white listing page the border alone left it reading as a
// disabled field rather than something worth opening.
const Card = styled.div`
  background: ${Colors.Semantic.BStock.background.lightBackground};
  border: 0.0625rem solid ${Colors.Semantic.BStock.default.lightBackground};
  border-radius: 0.5rem;
  padding: 0.9375rem 1rem;
  width: 100%;
`

// The design sits the dismiss control beside the headline and body rather
// than on a line of its own above them.
const CardHeader = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 0.625rem;
`

const Headlines = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.3125rem;
  min-width: 0;
`

const DismissButton = styled.button`
  background: none;
  border: none;
  flex: 0 0 auto;
  color: ${Colors.Semantic.Neutral.dark_grey};
  cursor: pointer;
  line-height: 1;
  padding: 0;
`

const Headline = styled.p`
  ${Typography.Subtitle2}
  margin: 0;
`

const Body = styled.p`
  ${Typography.Body4}
  margin: 0;
`

const Toggle = styled.button`
  ${Typography.Body4_Semibold}
  align-items: center;
  background: none;
  border: none;
  border-top: 0.0625rem solid ${Colors.Semantic.Neutral.medium_grey};
  color: ${Colors.Semantic.BStock.default.lightBackground};
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  margin-top: 0.75rem;
  padding: 0.6875rem 0 0;
  text-align: left;
  width: 100%;
`

const ToggleIcon = styled(FontAwesomeIcon)<{ $isExpanded: boolean }>`
  font-size: 0.625rem;
  transform: rotate(${({ $isExpanded }) => ($isExpanded ? '180deg' : '0deg')});
`

const Steps = styled.ol`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  list-style: none;
  margin: 0.75rem 0 0;
  padding: 0;
`

const Step = styled.li`
  display: flex;
  gap: 0.625rem;
`

const Ordinal = styled.span`
  ${Typography.Overline3}
  align-items: center;
  background: ${Colors.Semantic.BStock.default.lightBackground};
  border-radius: 50%;
  color: ${Colors.Semantic.Neutral.white};
  display: inline-flex;
  flex: 0 0 auto;
  height: 1.125rem;
  justify-content: center;
  /* Overline3's tracking would push a single digit off the circle's centre. */
  letter-spacing: 0;
  width: 1.125rem;
`

const StepTitle = styled.span`
  ${Typography.Body4_Semibold}
  display: block;
`

const StepBody = styled.span`
  ${Typography.Body4}
  color: ${Colors.Semantic.Neutral.dark_grey};
  display: block;
`

/**
 * Explains the two-payment model before a first-time buyer bids.
 *
 * Shown only to buyers with no Seller Collects history — including signed-out
 * visitors, who are new to it by definition. The steps are generated from the
 * storefront's own config rather than hardcoded per seller, so a storefront
 * that invoices differently reads correctly without a code change.
 */
export const SellerCollectsBanner = ({
  listing,
}: {
  listing:
    | OrchestratedAuctionListingDetails
    | OrchestratedOfferingListingDetails
}) => {
  const { formatMessage } = useIntl()
  const sellerCollectsEnabled = useFlag(
    FeatureFlags.SELLER_COLLECTS_BUYER_EXPERIENCE
  )
  const isSellerCollects =
    sellerCollectsEnabled && listing.sellerCollects === true

  const hasPriorOrder = useHasPriorSellerCollectsOrder({
    sellerAccountId: listing.accountId,
  })
  const isLoggedIn = useIsLoggedIn()
  const buyerAccountId = useDecodedAccessToken()?.accountId

  // The plan endpoint is authenticated (`requireAuth`), so asking for it while
  // signed out throws rather than returning nothing — see BUGS-5803.
  const {
    data: subscription,
    isLoading: isSubscriptionLoading,
    isError: isSubscriptionError,
  } = activeSellerSubscriptionServiceQuery.useQuery(listing.accountId, {
    enabled: isSellerCollects && isLoggedIn,
  })

  const [isDismissed, setIsDismissed] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const { trackSellerCollectsEvent } = useSellerCollectsAnalytics({
    sellerAccountId: listing.accountId,
    enabled: isSellerCollects,
  })

  // A dismissal taken before the buyer's token decoded has no key to live
  // under yet. Held here until one exists, so the read below cannot undo it.
  const dismissedBeforeIdResolved = useRef<string | undefined>(undefined)

  // Read after mount: sessionStorage does not exist while server-rendering,
  // and an initial value read from it would not survive hydration. Re-read on
  // either id, so moving to another seller's listing shows that seller's
  // banner rather than inheriting this one's dismissal.
  useEffect(() => {
    // Another seller's banner is its own question, so a pending dismissal
    // never carries across to one.
    if (dismissedBeforeIdResolved.current !== listing.accountId) {
      dismissedBeforeIdResolved.current = undefined
    }

    if (
      dismissedBeforeIdResolved.current === listing.accountId &&
      buyerAccountId !== undefined
    ) {
      dismissBanner(buyerAccountId, listing.accountId)
      dismissedBeforeIdResolved.current = undefined
      setIsDismissed(true)
      return
    }

    setIsDismissed(isBannerDismissed(buyerAccountId, listing.accountId))
  }, [buyerAccountId, listing.accountId])

  const dismiss = () => {
    setIsDismissed(true)
    // A signed-in buyer whose token has not decoded yet would be written under
    // the anonymous key. Remember it instead and write it once the id lands.
    if (isLoggedIn && buyerAccountId === undefined) {
      dismissedBeforeIdResolved.current = listing.accountId
      return
    }
    dismissBanner(buyerAccountId, listing.accountId)
  }

  // A signed-out visitor cannot resolve the plan, so the copy claims no fee
  // model rather than guessing one. An errored lookup is equally unknown.
  const feeModel = getFeeModel({
    isLoggedIn,
    isLoading: isSubscriptionLoading,
    isError: isSubscriptionError,
    buyersPremium: subscription?.buyersPremium,
  })

  const config = getSellerCollectsConfig(listing)
  // Read before the early returns below: a hook cannot be skipped on the
  // renders where the banner decides it has nothing to show.
  const methods = useAcceptedPaymentMethods(config?.acceptedPaymentMethods)
  const asksForProofOfPayment = useAsksForProofOfPayment(config, {
    storeFrontId: listing.storefrontId,
    sellerCollects: listing.sellerCollects,
  })

  if (!isSellerCollects || isDismissed) return null
  // Only a buyer who has never done this needs it explained.
  if (hasPriorOrder !== false) return null
  // The models say different things about money; claim none until one is known.
  if (feeModel === undefined) return null
  const sellerName =
    listing.seller.storefront?.name ?? listing.seller.account.displayName

  // The design closes the body with how the freight moves; the locale string
  // selects on this.
  const shippingMode = resolveShippingArrangedBy(config)
  const stepKeys = getSellerCollectsWinStepKeys(config, feeModel, {
    mergeSellerPaymentSteps: true,
    asksForProofOfPayment,
  }).map((key) =>
    // The merged step names the accepted methods; a storefront that lists none
    // gets the variant that stops before the dash rather than an empty clause.
    key === 'payInvoiced' && methods === undefined
      ? 'payInvoicedNoMethods'
      : key
  )
  // Shared with the shipping card and the tag, so one listing cannot be
  // described three ways on the same page.
  const saleKind = getListingSaleKind(listing)

  // Reported on the way open only: collapsing is not a second expansion.
  const toggleSteps = () => {
    if (!isExpanded) {
      trackSellerCollectsEvent(
        SellerCollectsEvent.EducationExpanded,
        sellerCollectsListingContext(listing, buyerPremiumAppliesFor(feeModel))
      )
    }
    setIsExpanded((open) => !open)
  }

  const values = {
    sellerName: sellerName || (
      <FormattedMessage id="Listing.tags.sellerCollects.theSeller" />
    ),
    methods: methods ?? '',
    // LDP-2: the body's shipping sentence (or lack of one) depends on who
    // arranges freight, same branching as the shipping step below — read via
    // an ICU select in the locale string rather than a second message id per
    // fee model, matching this file's "one string unless the tense differs"
    // convention (sellerCollectsBannerCopy.ts).
    shippingMode: shippingMode ?? 'UNCONFIGURED',
  }

  return (
    <Card
      role="region"
      aria-labelledby={HEADLINE_ID}
      data-testid="seller-collects-banner"
    >
      <CardHeader>
        <Headlines>
          <Headline id={HEADLINE_ID}>
            <FormattedMessage
              id={bannerCopyId(`headline.${feeModel}`)}
              values={values}
            />
          </Headline>
          <Body>
            <FormattedMessage
              id={bannerCopyId(`body.${feeModel}`, saleKind)}
              values={values}
            />
          </Body>
        </Headlines>
        <DismissButton
          type="button"
          onClick={dismiss}
          data-testid="seller-collects-banner-dismiss"
          aria-label={formatMessage({ id: bannerCopyId('dismiss') })}
        >
          <FontAwesomeIcon icon={faXmark} size="xl" />
        </DismissButton>
      </CardHeader>
      <Toggle
        type="button"
        aria-expanded={isExpanded}
        aria-controls={STEPS_ID}
        onClick={toggleSteps}
        data-testid="seller-collects-banner-toggle"
      >
        <FormattedMessage
          id={bannerCopyId(isExpanded ? 'toggleExpanded' : 'toggle', saleKind)}
        />
        <ToggleIcon
          icon={faChevronDown}
          $isExpanded={isExpanded}
          aria-hidden="true"
        />
      </Toggle>
      {isExpanded && (
        <Steps id={STEPS_ID} data-testid={STEPS_ID}>
          {stepKeys.map((key, index) => (
            <Step key={key}>
              <Ordinal aria-hidden="true">{index + 1}</Ordinal>
              <div>
                <StepTitle>
                  <FormattedMessage
                    id={bannerCopyId(`steps.titles.${key}`, saleKind)}
                    values={values}
                  />
                </StepTitle>
                <StepBody>
                  <FormattedMessage
                    id={sellerCollectsWinStepId(key, saleKind)}
                    values={values}
                  />
                </StepBody>
              </div>
            </Step>
          ))}
        </Steps>
      )}
    </Card>
  )
}
